import { useCallback, useEffect, useRef, useState } from 'react';
import { action, subscribe } from './api.ts';

/**
 * Loads an action's result and reloads it when one of the given live events arrives.
 * Returns `live` = false while the event stream is reconnecting.
 */
export function useLiveAction<T>(name: string, input: Record<string, unknown>, topics: string[], filter?: (event: string, data: any) => boolean) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [live, setLive] = useState<boolean | null>(null);
  const key = JSON.stringify(input);
  const filterRef = useRef(filter);
  filterRef.current = filter;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reload = useCallback(async () => {
    try {
      setData(await action<T>(name, JSON.parse(key)));
      setError(null);
    } catch (err) {
      setError(err);
    }
  }, [name, key]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (topics.length === 0) return;
    return subscribe(
      topics,
      (event, payload) => {
        if (event === 'ready') return;
        if (filterRef.current && !filterRef.current(event, payload)) return;
        // Several events arrive together after one change: reload once.
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => void reload(), 120);
      },
      (ok) => {
        setLive(ok);
        if (ok) void reload();
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topics.join(','), reload]);

  return { data, setData, error, live, reload };
}
